import React, { useId, useMemo, useState } from 'react';
import { button, input } from './editors';
import { CoverEditor } from './cover-editor';
import {
  conflict,
  decisionSender,
  type Cover,
  type Report,
  type ReportIssue,
  type Request,
} from './model';
import {
  inspectableCoverUrl,
  relatedReportGroupComplete,
  reportDecisionBody,
  type ReportAction,
} from './report-decision';
import { REPORT_COPY, reportLocale, reportReasonLabel, type ReportLocale } from './report-copy';

export function ReportCard({
  report,
  request,
  done,
  reload = done,
  locale = reportLocale(),
}: {
  report: Report;
  request: Request;
  done(message?: string): void;
  reload?(): void;
  locale?: ReportLocale;
}) {
  const copy = REPORT_COPY[locale];
  const send = useMemo(() => decisionSender(request), [request]);
  const [busy, setBusy] = useState(false),
    [stale, setStale] = useState(false),
    [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const act = async (body: NonNullable<ReturnType<typeof reportDecisionBody>>) => {
    if (busy || stale || uploading) return;
    setBusy(true);
    setError('');
    try {
      await send(`/reports/${encodeURIComponent(report.id)}/decision`, body);
      done(copy.saved);
    } catch (e) {
      const changed = conflict(e);
      setStale(changed);
      setError(changed ? copy.stale : copy.failed);
    } finally {
      setBusy(false);
    }
  };
  const issues = report.issues ?? [];
  return (
    <article className="mt-5 border-t border-line/65 pt-5" lang={locale} aria-busy={busy}>
      <h3 className="font-semibold">
        {copy.report} · {copy.statuses[report.status]}
      </h3>
      <p className="mt-2 break-all text-xs text-ink-muted">
        {report.id} · {copy.version} {report.version}
      </p>
      {!issues.length && <p className="my-3 text-sm">{reportReasonLabel(report.reason, locale)}</p>}
      {report.details && (
        <p className="my-3 whitespace-pre-wrap text-sm leading-6 text-ink-soft">{report.details}</p>
      )}
      {report.evidence && (
        <div className="mt-3 rounded-lg bg-thread-soft p-3 text-sm">
          <h4 className="font-semibold">{copy.evidence}</h4>
          <p className="mt-2 whitespace-pre-wrap leading-6">{report.evidence}</p>
        </div>
      )}
      {report.targetBookId && (
        <p className="mt-2 text-sm">
          {copy.suggestedDuplicate}: {report.targetBookId}
        </p>
      )}
      {report.moderatorMessage && (
        <p className="mt-2 whitespace-pre-wrap text-sm">
          {copy.resolution}: {report.moderatorMessage}
        </p>
      )}
      {issues.map((issue) => (
        <ReportIssueReview
          key={issue.id}
          report={report}
          issue={issue}
          request={request}
          locale={locale}
          disabled={busy || stale || uploading}
          uploading={uploading}
          setUploading={setUploading}
          decide={act}
        />
      ))}
      {!issues.length && report.status === 'pending' && (
        <p role="status" className="mt-4 text-sm text-warning">
          {copy.legacy}
        </p>
      )}
      {busy && (
        <p role="status" className="mt-4 text-sm text-ink-muted">
          {copy.saving}
        </p>
      )}
      {error && (
        <p role="alert" className="mt-4 whitespace-pre-wrap text-sm text-warning">
          {error}
        </p>
      )}
      {(error || (!issues.length && report.status === 'pending')) && (
        <button
          type="button"
          className={`${button} mt-4`}
          disabled={busy || uploading}
          onClick={reload}
        >
          {copy.reload}
        </button>
      )}
    </article>
  );
}

function ReportIssueReview({
  report,
  issue,
  request,
  locale,
  disabled,
  uploading,
  setUploading,
  decide,
}: {
  report: Report;
  issue: ReportIssue;
  request: Request;
  locale: ReportLocale;
  disabled: boolean;
  uploading: boolean;
  setUploading(value: boolean): void;
  decide(body: NonNullable<ReturnType<typeof reportDecisionBody>>): Promise<void>;
}) {
  const id = useId(),
    copy = REPORT_COPY[locale];
  const [action, setAction] = useState<ReportAction>('dismiss'),
    [message, setMessage] = useState('');
  const [resolveRelated, setResolveRelated] = useState(false);
  const [cover, setCover] = useState<Cover>({ policy: 'moderator' });
  const [loadedUrl, loaded] = useState<string | null>(null),
    [failed, setFailed] = useState(false);
  const [description, setDescription] = useState(issue.content?.description ?? '');
  const body = reportDecisionBody(report, issue, {
    action,
    moderatorMessage: message,
    resolveRelated,
    cover,
    loadedCoverUrl: loadedUrl,
    description,
  });
  const content = issue.target !== 'book',
    pending = issue.status === 'pending' && report.status === 'pending';
  const versionAvailable =
    !content ||
    Boolean(
      issue.versionKey &&
      Number.isSafeInteger(issue.contentVersion) &&
      (issue.contentVersion ?? 0) > 0,
    );
  const related = issue.relatedReports ?? [];
  const groupComplete = relatedReportGroupComplete(issue);
  const relatedCount = issue.relatedReportCount ?? related.length;
  const actions: ReportAction[] = content
    ? ['dismiss', 'suppress', 'replace']
    : ['dismiss', 'resolve'];
  return (
    <section
      className="mt-5 rounded-xl border border-line/65 bg-panel-raised p-4 sm:p-5"
      aria-labelledby={`${id}-title`}
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h4 id={`${id}-title`} className="font-serif text-xl">
          {copy.targets[issue.target]}
        </h4>
        <span className="text-sm font-semibold text-ink-muted">
          {issue.state ? copy.states[issue.state] : copy.statuses[issue.status]}
        </span>
      </div>
      <ul className="my-3 list-disc space-y-1 pl-5 text-sm text-ink-soft">
        {issue.reasons.map((reason) => (
          <li key={reason}>{reportReasonLabel(reason, locale)}</li>
        ))}
      </ul>
      {issue.details && (
        <div className="mt-3">
          <h5 className="text-xs font-semibold text-ink-muted">{copy.details}</h5>
          <p className="mt-1 whitespace-pre-wrap text-sm leading-6">{issue.details}</p>
        </div>
      )}
      {issue.status !== 'pending' && (
        <p className="mt-3 text-sm font-semibold">
          {copy.resolution}: {copy.statuses[issue.status]}
        </p>
      )}
      {issue.moderatorMessage && (
        <p className="mt-2 whitespace-pre-wrap text-sm leading-6">{issue.moderatorMessage}</p>
      )}
      {issue.status !== 'pending' && issue.updatedAt && (
        <p className="mt-2 text-xs text-ink-muted">
          {new Date(issue.updatedAt).toLocaleString(locale)}
        </p>
      )}
      {issue.replacement && (
        <details className="mt-3 text-sm">
          <summary className="cursor-pointer font-semibold">{copy.replacement}</summary>
          <p className="mt-2 whitespace-pre-wrap break-all leading-6">
            {issue.replacement.description ?? issue.replacement.url}
          </p>
          {issue.replacement.rightsNote && (
            <p className="mt-2 whitespace-pre-wrap">{issue.replacement.rightsNote}</p>
          )}
        </details>
      )}
      {content && <ReportedContent issue={issue} locale={locale} />}
      {content && (
        <details className="mt-4 text-sm">
          <summary className="cursor-pointer font-semibold">
            {copy.related} ({relatedCount})
          </summary>
          {related.length ? (
            <ul className="mt-3 space-y-3">
              {related.map((item) => (
                <li key={item.id} className="rounded-lg border border-line/65 p-3">
                  <p className="break-all text-xs text-ink-muted">
                    {item.id} ·{' '}
                    {copy.statuses[item.status as keyof typeof copy.statuses] ?? item.status} ·{' '}
                    {new Date(item.createdAt).toLocaleDateString(locale)}
                  </p>
                  <p className="mt-2">
                    {item.reasons.map((reason) => reportReasonLabel(reason, locale)).join(' · ')}
                  </p>
                  <p className="mt-2 whitespace-pre-wrap leading-6">{item.details}</p>
                </li>
              ))}
            </ul>
          ) : relatedCount === 0 ? (
            <p className="mt-2 text-ink-muted">{copy.noRelated}</p>
          ) : null}
          {!groupComplete && (
            <p role="status" className="mt-3 text-warning">
              {copy.relatedTruncated}
            </p>
          )}
        </details>
      )}
      {pending && (
        <fieldset
          className="mt-5 space-y-4 border-t border-line/65 pt-5"
          disabled={disabled || !versionAvailable}
        >
          <label className="block text-sm font-semibold">
            {copy.decision}
            <select
              className={input}
              value={action}
              onChange={(event) => setAction(event.target.value as ReportAction)}
            >
              {actions.map((value) => (
                <option key={value} value={value}>
                  {copy[value]}
                </option>
              ))}
            </select>
          </label>
          <p className="text-sm leading-6 text-ink-muted">{copy[`${action}Effect`]}</p>
          {action === 'replace' && issue.target === 'cover' && (
            <CoverEditor
              request={request}
              value={cover}
              change={setCover}
              loadedUrl={loadedUrl}
              loaded={loaded}
              failed={failed}
              setFailed={setFailed}
              uploading={uploading}
              setUploading={setUploading}
              replacementOnly
              locale={locale}
            />
          )}
          {action === 'replace' && issue.target === 'description' && (
            <label className="block text-sm font-semibold">
              {copy.replaceDescription}
              <textarea
                className={input}
                rows={8}
                maxLength={12000}
                value={description}
                onChange={(event) => setDescription(event.target.value)}
              />
            </label>
          )}
          {action === 'replace' && (
            <p className="text-xs leading-5 text-ink-muted">{copy.replacementRequired}</p>
          )}
          <label className="block text-sm font-semibold">
            {copy.reason}
            <textarea
              className={input}
              rows={3}
              maxLength={1000}
              aria-describedby={`${id}-reason-hint`}
              value={message}
              onChange={(event) => setMessage(event.target.value)}
            />
          </label>
          <p id={`${id}-reason-hint`} className="text-xs text-ink-muted">
            {copy.reasonHint}
          </p>
          {content && (related.some((item) => item.status === 'pending') || !groupComplete) && (
            <div>
              <label className="flex items-start gap-3 text-sm">
                <input
                  type="checkbox"
                  className="mt-1 h-4 w-4 shrink-0 accent-thread"
                  checked={resolveRelated}
                  disabled={!groupComplete}
                  onChange={(event) => setResolveRelated(event.target.checked)}
                />
                <span>{copy.relatedGroup}</span>
              </label>
              <p className="mt-2 text-xs leading-5 text-ink-muted">
                {!groupComplete
                  ? copy.relatedTruncated
                  : resolveRelated
                    ? copy.relatedHelp
                    : copy.singleHelp}
              </p>
            </div>
          )}
          <button
            type="button"
            className={action === 'suppress' ? 'admin-button-danger' : 'admin-button-primary'}
            disabled={!body}
            onClick={() => body && void decide(body)}
          >
            {copy.save}
          </button>
        </fieldset>
      )}
      {pending && !versionAvailable && (
        <p role="status" className="mt-4 text-sm text-warning">
          {copy.pendingVersion}
        </p>
      )}
    </section>
  );
}

function ReportedContent({ issue, locale }: { issue: ReportIssue; locale: ReportLocale }) {
  const copy = REPORT_COPY[locale],
    [open, setOpen] = useState(false);
  const url = inspectableCoverUrl(issue.content?.url);
  return (
    <details className="mt-4 text-sm" onToggle={(event) => setOpen(event.currentTarget.open)}>
      <summary className="cursor-pointer font-semibold">{copy.reportedContent}</summary>
      <p className="mt-3 text-xs leading-5 text-ink-muted">{copy.snapshot}</p>
      {issue.target === 'cover' ? (
        <>
          {open && url && (
            <img
              src={url}
              alt={copy.coverAlt}
              loading="lazy"
              referrerPolicy="no-referrer"
              className="mt-4 max-h-80 max-w-60 rounded-lg bg-panel p-3 object-contain"
            />
          )}
          {!url && (
            <p className="mt-3 text-ink-muted">
              {issue.content?.url ? copy.unsafeUrl : copy.noContent}
            </p>
          )}
          <p className="mt-3 text-xs leading-5 text-ink-muted">{copy.mutableUrl}</p>
        </>
      ) : (
        <p className="mt-3 max-h-96 overflow-auto whitespace-pre-wrap rounded-lg bg-panel p-4 leading-6">
          {issue.content?.description ?? copy.noContent}
        </p>
      )}
      <dl className="mt-4 space-y-2 text-xs text-ink-muted">
        <div>
          <dt className="font-semibold">{copy.versionKey}</dt>
          <dd className="mt-1 break-all">{issue.versionKey ?? '—'}</dd>
        </div>
        <div>
          <dt className="font-semibold">{copy.contentVersion}</dt>
          <dd>{issue.contentVersion ?? '—'}</dd>
        </div>
        <div>
          <dt className="font-semibold">{copy.identity}</dt>
          <dd className="mt-1 whitespace-pre-wrap break-all">
            {JSON.stringify(issue.identity ?? {}, null, 2)}
          </dd>
        </div>
      </dl>
    </details>
  );
}
